import { useMemo } from 'react';
import P from 'prop-types';
import Lottie from 'lottie-react';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
import { Text } from '@nerdwallet/currency';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  stringTextAreaSpec,
  stringSelectSpec,
  stringInputSpec,
  wrapper,
  group,
  numberInputSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';
import { interactivity, isJsonLottieString, animationModes } from './lib';
import { LottieIcon } from './lib/lottie-icon';
import UniversalContainer, {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from '../universal-container';

const lottiePlayerId = 'lottie-player';

function renderAnimation(
  animationMode,
  animationData,
  visibility,
  frames,
  dimensions
) {
  return (
    <Lottie
      width={dimensions?.width}
      height={dimensions?.height}
      autoPlay
      interactivity={interactivity(animationMode, visibility, frames)}
      animationData={animationData}
    />
  );
}

const LottieAnimation = ({
  container,
  animationData,
  animationMode,
  width,
  height,
  desktopWidth,
  desktopHeight,
  tabletWidth,
  tabletHeight,
  mobileWidth,
  mobileHeight,
  visibility,
  frames,
  size,
  dimensionsPerViewport,
}) => {
  // Because of page re-renders AnimationData reference is lost and can lead into errors.
  // To prevent this we memoize it
  const validAnimationData = useMemo(
    () => isJsonLottieString(animationData),
    [animationData]
  );

  const showAnimation = animationData && validAnimationData;
  const showInvalidJsonMessage = animationData && !validAnimationData;

  const visibilityObj = useMemo(
    () => ({ x: visibility.x, y: visibility.y }),
    [visibility.x, visibility.y]
  );

  const framesObj = useMemo(
    () => ({ x: frames.x, y: frames.y }),
    [frames.x, frames.y]
  );

  const onScroll = useMemo(
    () =>
      renderAnimation(
        animationModes.SEEK,
        validAnimationData,
        visibilityObj,
        framesObj
      ),
    [framesObj, validAnimationData, visibilityObj]
  );

  const whenVisible = useMemo(
    () =>
      renderAnimation(
        animationModes.PLAY,
        validAnimationData,
        visibilityObj,
        framesObj
      ),
    [framesObj, validAnimationData, visibilityObj]
  );

  // Every time "dimensionsPerViewport' changes we'll re-map dimensions to match the viewport.
  // If viewport is not provided we'll used the last one
  const dimensions = useMemo(
    () =>
      dimensionsPerViewport
        ? {
            lg: { width: desktopWidth, height: desktopHeight },
            md: {
              width: tabletWidth || desktopWidth,
              height: tabletHeight || desktopHeight,
            },
            sm: {
              width: mobileWidth || tabletWidth,
              height: mobileHeight || tabletHeight,
            },
          }[size]
        : { width, height },
    [
      desktopHeight,
      desktopWidth,
      dimensionsPerViewport,
      height,
      mobileHeight,
      mobileWidth,
      size,
      tabletHeight,
      tabletWidth,
      width,
    ]
  );

  return (
    <UniversalContainer {...container}>
      <div
        id={lottiePlayerId}
        data-testid={lottiePlayerId}
        style={{
          width: dimensions?.width || 300,
          height: dimensions?.height || 300,
        }}
      >
        {!animationData && <LottieIcon />}
        {showInvalidJsonMessage && <Text>Invalid JSON value!</Text>}
        {/*
         Currently we are not able to get different output of Lottie by only
         changing its properties, we have to create different instances with
         its own settings then output it as we see below
       */}
        {showAnimation && animationMode === animationModes.PLAY && whenVisible}
        {showAnimation && animationMode === animationModes.SEEK && onScroll}
      </div>
    </UniversalContainer>
  );
};

LottieAnimation.propTypes = {
  container: universalContainerPropTypes,
  animationData: P.string,
  animationMode: P.string,
  width: P.number,
  height: P.number,
  desktopWidth: P.number,
  desktopHeight: P.number,
  tabletWidth: P.number,
  tabletHeight: P.number,
  mobileWidth: P.number,
  mobileHeight: P.number,
  size: P.string,
  dimensionsPerViewport: P.bool,
  visibility: P.shape({
    x: P.string,
    y: P.string,
  }),
  frames: P.shape({
    x: P.string,
    y: P.string,
  }),
};

LottieAnimation.defaultProps = {
  container: universalContainerDefaultProps,
  animationData: '',
  animationMode: animationModes.SEEK,
  width: 300,
  height: 300,
  visibility: {
    x: '0.5',
    y: '1.0',
  },
  frames: {
    x: '0.5',
    y: '1.0',
  },
};

const isTestEnv = process?.env?.NODE_ENV;

const optionsObj = {
  disableGroup: false,
};
// in order to make the test work properly we set "disableGroup" true when in test env
optionsObj.disableGroup = !isTestEnv;

LottieAnimation.getEditingConfig = (
  componentDocument,
  fieldLabel,
  fieldName,
  options = {
    ...optionsObj,
  }
) => {
  // because we need to know when the component is used standalone or in a <Grid>
  // when used in standalone the props will be in componentDocument.props.myProps
  // when used in <Grid> the props will be in componentDocument.props.nested.field.name.myProps
  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const initialAnimationMode = get(
    componentDocument,
    `props.${getInputName('animationMode')}`, // When used inside of Grid getInputName will handle props path
    animationModes.SEEK
  );

  const visibilityInputFields = ['x', 'y'].map((axis) =>
    stringInputSpec(
      getInputName(`visibility.${axis}`),
      `Visibility ${axis.toUpperCase()}`
    )
  );

  const framesInputFields = ['x', 'y'].map((axis) =>
    stringInputSpec(
      getInputName(`frames.${axis}`),
      `Frames ${axis.toUpperCase()}`
    )
  );

  const whenVisibleInputConfig =
    initialAnimationMode === animationModes.PLAY
      ? [...visibilityInputFields]
      : [];

  const onScrollInputConfig =
    initialAnimationMode === animationModes.SEEK
      ? [...visibilityInputFields, ...framesInputFields]
      : [];

  const outputDimensionsPerViewPort = get(
    componentDocument,
    `props.${getInputName('dimensionsPerViewport')}`,
    false
  );

  const dimensionsInputConfig = outputDimensionsPerViewPort
    ? [
        numberInputSpec(getInputName('desktopWidth'), 'Desktop Width'),
        numberInputSpec(getInputName('desktopHeight'), 'Desktop Height'),
        numberInputSpec(getInputName('tabletWidth'), 'Tablet Width'),
        numberInputSpec(getInputName('tabletHeight'), 'Tablet Height'),
        numberInputSpec(getInputName('mobileWidth'), 'Mobile Width'),
        numberInputSpec(getInputName('mobileHeight'), 'Mobile Height'),
      ]
    : [
        numberInputSpec(getInputName('width'), 'Width'),
        numberInputSpec(getInputName('height'), 'Height'),
      ];

  const editorPanelOptions = [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      getInputName('container')
    ),
    stringTextAreaSpec(getInputName('animationData'), 'Animation Data'),
    stringSelectSpec(getInputName('animationMode'), 'Animation Mode', [
      { label: 'On Scroll', value: animationModes.SEEK },
      { label: 'When Visible', value: animationModes.PLAY },
    ]),
    boolCheckboxSpec(
      getInputName('dimensionsPerViewport'),
      'Unique Dimensions Per Viewport?'
    ),
    ...dimensionsInputConfig,
    ...whenVisibleInputConfig,
    ...onScrollInputConfig,
  ];

  if (options.disableGroup) {
    return [wrapper([...editorPanelOptions])];
  }
  return [group(fieldLabel, [...editorPanelOptions])];
};

export default connect(
  createStructuredSelector({
    size: (state) => state?.mediaQuery,
  })
)(LottieAnimation);
